/**
 * API notifications-service для колокольчика кабинета агента (DEV-1017). Ручки те же,
 * что в crm-vue: лента, счётчик непрочитанных, отметка прочитанным. Настройки/каналы
 * тут не нужны (колокольчик), только чтение + read.
 */
import { apiClient } from '@/api'
import {
  NOTIFICATION_APP,
  type ChannelPreference,
  type Notification,
  type NotificationsListResponse,
  type TypePreference,
} from '../types/notifications.types'

const BASE_PATH = '/api/v1/notifications'

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Терпимый маппер: notifications-service отдаёт camelCase, но метаданные — строкой. */
export function mapNotification(raw: any): Notification {
  let metadata: Record<string, unknown> = {}
  if (raw?.metadata) {
    try {
      metadata = typeof raw.metadata === 'string' ? JSON.parse(raw.metadata) : raw.metadata
    } catch {
      metadata = {}
    }
  }

  return {
    id: raw.id,
    notificationType: raw.notificationType ?? raw.notification_type,
    title: raw.title,
    body: raw.body,
    metadata,
    channelsSent: raw.channelsSent ?? raw.channels_sent ?? [],
    isRead: raw.isRead ?? raw.is_read ?? false,
    createdAt: raw.createdAt ?? raw.created_at,
    readAt: raw.readAt ?? raw.read_at ?? null,
    app: raw.app ?? NOTIFICATION_APP,
    url: raw.url ?? '',
  }
}

export const notificationsApi = {
  /** GET /api/v1/notifications — история (пагинация PageRequest, uint64 → строки). */
  async listNotifications(page = 1, pageSize = 20): Promise<NotificationsListResponse> {
    const { data } = await apiClient.get(BASE_PATH, {
      params: { 'pagination.page': page, 'pagination.perPage': pageSize, app: NOTIFICATION_APP },
    })
    return {
      notifications: (data.notifications ?? []).map(mapNotification),
      total: parseInt(data.pagination?.total ?? '0', 10) || 0,
      page: parseInt(data.pagination?.page ?? '0', 10) || page,
      pageSize: parseInt(data.pagination?.perPage ?? '0', 10) || pageSize,
    }
  },

  async markRead(notificationId: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/${notificationId}/read`)
  },

  // app обязателен и здесь, и в счётчике: иначе «прочитать всё» в кабинете
  // погасило бы бейджи CRM и трекера, а счётчик считал бы чужое (DEV-1965).
  async markAllRead(): Promise<number> {
    const { data } = await apiClient.post(`${BASE_PATH}/read-all`, { app: NOTIFICATION_APP })
    return data.markedCount ?? 0
  },

  async getUnreadCount(): Promise<number> {
    const { data } = await apiClient.get(`${BASE_PATH}/unread-count`, {
      params: { app: NOTIFICATION_APP },
    })
    return data.count ?? 0
  },

  // --- Настройки (страница настроек кабинета) ---

  async getPreferences(): Promise<ChannelPreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences`)
    return data.preferences ?? []
  },

  async updatePreferences(preferences: ChannelPreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences`, { preferences })
  },

  async getEventPreferences(): Promise<TypePreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences/events`)
    return data.preferences ?? []
  },

  async updateEventPreferences(preferences: TypePreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences/events`, { preferences })
  },
}
